import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocale } from "../primitives/useLocale";
import {
  assembleCoachDashboard,
  buildCoachAthleteCard,
  fetchAthleteTargets,
  parseProgramSummaries,
  type CoachAthleteCard,
  type CoachAthleteInput,
  type CoachDashboardData,
  type CoachUnavailableAthlete,
  type CoachVisibleProfile,
  type Exercise,
  type SessionResult,
} from "@heraion/shared";

import type { ProgressApiFetch } from "../Progress/types";

type SessionResultRow = SessionResult & { startedAtMs: number };

export type CoachDashboardState = {
  loading: boolean;
  // With `data`: the last refresh failed in part or whole, and what is on
  // screen is the last good read. Without: nothing could be loaded.
  error: boolean;
  data: CoachDashboardData | null;
  // Re-run the fan-out. The error copy tells the coach to refresh and retry, so
  // a host on a surface with no browser reload (mobile) needs something real to
  // wire that promise to — pull-to-refresh, a retry button.
  refresh: () => void;
};

// CO-8 coach dashboard data hook. Reads each athlete's public bundle through the
// CO-11 edge-gated routes (profile name+config / full programs / session
// aggregates / catalog — all read-only, health fields already stripped
// server-side) and feeds them to the pure buildCoachDashboard aggregator. Family
// scale (≤~8 athletes) → a per-athlete fan-out of 4 small GETs is comfortable;
// revisit with a batch endpoint if a roster ever grows large.
//
// The ROSTER is a parameter, not a fetch (CN-coach-edges-fetch): both hosts
// already hold the caller's edges — web from useCoachStatus, mobile from the
// shell-mounted CoachEdgesContext — so self-fetching /api/me/coach-edges here
// meant a duplicate read per mount. Who-do-I-coach stays in exactly one place,
// shared's groupCoachEdges().athletesActive; this hook's job is only "build the
// dashboard for THESE athletes".
//
// `athleteSubs: null` means "the roster hasn't resolved yet" and holds the hook
// in loading. It is NOT the same as `[]`, and the difference is load-bearing: the
// coach experience only exists for someone with ≥1 active athlete, so an empty
// roster can never legitimately mean "no athletes" — it means the edges are
// still in flight. Handing `[]` to buildCoachDashboard yields a dashboard of
// zeroes, which is a wrong answer rendered confidently.
//
// Every per-athlete fetch is fail-SOFT: an athlete who hasn't saved a profile
// (404 — they were invited + trained but never opened Settings) or whose other
// reads hiccup must NOT sink the whole team view. Each read degrades to a safe
// default (missing profile → fallback name, missing programs → empty), so a
// partial athlete still appears with whatever data loaded.
// ⛔ Except session-results (07/10: a stuck token refresh made every athlete
// "never trained"). A failed read marks the athlete unavailable, and on a
// refresh the athlete's last good card stays on screen instead.
export function useCoachDashboard(
  apiFetch: ProgressApiFetch,
  athleteSubs: ReadonlyArray<string> | null,
): CoachDashboardState {
  const { t } = useTranslation();
  const lang = useLocale();
  const [state, setState] = useState<Omit<CoachDashboardState, "refresh">>({
    loading: true,
    error: false,
    data: null,
  });
  const [reloads, setReloads] = useState(0);
  const refresh = useCallback(() => setReloads((n) => n + 1), []);
  // Each athlete's last good card (built at its read's time), for the
  // current roster only. The card, not the read: the catalog and the history
  // are not kept per athlete. The map is built once.
  const lastGoodRef = useRef<Map<string, CoachAthleteCard> | null>(null);
  if (lastGoodRef.current === null) lastGoodRef.current = new Map();
  const lastGood = lastGoodRef.current;

  // The roster's IDENTITY, not the array's: hosts build `athleteSubs` by mapping
  // their edges, so a fresh array arrives on every parent render. Keying the
  // effect on the joined subs re-fans-out when the roster actually changes and
  // stays put when it doesn't. Sorted because the edge read carries no order
  // guarantee and a reshuffled-but-identical roster is not a change worth a
  // re-fan-out; buildCoachDashboard sorts the cards by priority regardless.
  // null (roster unresolved) is its own key, distinct from the empty roster.
  const rosterKey = athleteSubs ? [...athleteSubs].sort().join(",") : null;

  useEffect(() => {
    let alive = true;
    // Keep whatever is already on screen while re-fanning-out: a pull-to-refresh
    // that blanks the roster back to the loading gate is a reset, not a refresh.
    // First mount has nothing to keep, so this still starts empty.
    setState((s) => ({ ...s, loading: true, error: false }));
    // Roster not resolved yet: stay loading rather than fan out over nobody and
    // report a confident dashboard of zeroes.
    if (athleteSubs === null) return;

    const fallbackName = t("coach.dashboard.unnamedAthlete", {
      defaultValue: "Athlete",
    });
    // An athlete who left the roster takes his last card with him.
    const roster = new Set(athleteSubs);
    for (const sub of [...lastGood.keys()]) {
      if (!roster.has(sub)) lastGood.delete(sub);
    }

    // What the coach sees, one row per athlete of the CURRENT roster: the
    // fresh card, else his last good one, else "not loaded". Never a card of
    // someone who left. Nobody loaded at all is the error state, with its retry.
    const show = (fresh: ReadonlyArray<CoachAthleteInput>) => {
      const bySub = new Map(fresh.map((input) => [input.sub, input]));
      const nowMs = Date.now();
      const cards: CoachAthleteCard[] = [];
      const unavailable: CoachUnavailableAthlete[] = [];
      let kept = 0;
      for (const sub of athleteSubs) {
        const input = bySub.get(sub);
        if (input && !input.unavailable) {
          const card = buildCoachAthleteCard(input, nowMs, lang);
          lastGood.set(sub, card);
          cards.push(card);
          continue;
        }
        const good = lastGood.get(sub);
        if (good) {
          kept += 1;
          cards.push(good);
        } else unavailable.push({ sub, name: input?.name ?? fallbackName });
      }
      if (cards.length === 0 && unavailable.length > 0) {
        return { loading: false, error: true, data: null };
      }
      const data = assembleCoachDashboard(cards, unavailable, athleteSubs.length);
      return { loading: false, error: kept > 0, data };
    };

    // Every read below degrades on its own, so the catch is for a fault in
    // the code: nothing trustworthy to show, the error state with its retry.
    const fanOut = async () => {
      try {
        const inputs = await Promise.all(
          athleteSubs.map(async (sub): Promise<CoachAthleteInput> => {
            const q = `athleteSub=${encodeURIComponent(sub)}`;
            const [profile, programs, sr, catalog, athleteTargets] =
              await Promise.all([
                apiFetch<CoachVisibleProfile>(
                  `/api/profiles/${encodeURIComponent(sub)}`,
                ).catch(() => null),
                apiFetch<unknown>(`/api/me/programs?${q}`)
                  .then(parseProgramSummaries)
                  .catch(() => []),
                apiFetch<{ items: SessionResultRow[] }>(
                  `/api/session-results?${q}`,
                ).catch(() => null),
                apiFetch<Exercise[]>(`/api/exercises?${q}`).catch(() => []),
                // ME-3: the athlete's own targets, so the card's adherence
                // scores against the same goals the athlete's Home meter does.
                fetchAthleteTargets(apiFetch, sub),
              ]);
            return {
              sub,
              name: profile?.name ?? fallbackName,
              currentProgramId: profile?.currentProgramId,
              sessions: (sr?.items ?? []).map((r) => ({
                result: r,
                startedAtMs: r.startedAtMs,
              })),
              unavailable: sr === null,
              catalog,
              programs,
              athleteTargets,
            };
          }),
        );
        if (alive) setState(show(inputs));
      } catch {
        if (alive) setState({ loading: false, error: true, data: null });
      }
    };
    fanOut();

    return () => {
      alive = false;
    };
    // `rosterKey` + `t` are the real inputs: apiFetch is a stable per-surface
    // seam (each host injects its own token-bearing client once) and athleteSubs
    // is re-created every render, so widening the deps would re-fan-out on every
    // parent render. See useWindowSets for the house idiom.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rosterKey, t, reloads]);

  return { ...state, refresh };
}
